import axiosInstance from "./axiosInstance";
import * as Sentry from "@sentry/browser";

export const saveAuthorBook = async (bookInfo) => {
  try {
    const payload = {
      user_manuscript_name: bookInfo.book_title,
      author_name: bookInfo.author_name,
      user_genres: bookInfo.book_genre,
      user_logline: bookInfo.logline,
      user_author_info: bookInfo.author_info,
      user_keywords: bookInfo.keywords,
      discovery: true,
    };

    const response = await axiosInstance.post("/manuscripts", payload);
    console.log("Book info saved. response:", response);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const uploadManuscript = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await axiosInstance.post(`/manuscripts/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getAuthorManuscripts = async () => {
  try {
    const response = await axiosInstance.get("/manuscripts");
    //console.log("Author manuscripts retrieved:", response);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to retrieve manuscripts:", error);
    throw error;
  }
};

export const getManuscriptDetails = async (book_id) => {
  try {
    const response = await axiosInstance.get(`/manuscripts/${book_id}`);
    //console.log("Manuscripts details retrieved:", response);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to retrieve manuscript details", error);
    throw error;
  }
};

export const startUpgrade = async (plan = "monthly") => {
  const returnToUrl = `${window.location.origin}/`; // return to the home page after payment
  try {
    const response = await axiosInstance.post(
      "/payments/create-checkout-session",
      {
        return_to: returnToUrl,
        plan,
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to create checkout session");
    }

    console.log("Checkout session created:", response.data);
    window.location.href = response.data.url;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error creating checkout session:", error);
    throw error;
  }
};

export const openPaymentSettings = async (userId) => {
  console.log(`Open payment settings for user ${userId} clicked`);
  const returnToUrl = window.location.href; // return to the current page after updating payment settings
  try {
    const { data } = await axiosInstance.post(
      "/payments/create-customer-portal-session",
      {
        return_to: returnToUrl,
      },
    );
    window.location.href = data.url;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error creating customer portal session:", error);
    alert("Failed to load payment settings. Please try again later.");
  }
};

export const addManuscript = async (bookInfo) => {
  try {
    const uploadFileResponse = await uploadManuscript(bookInfo.book_file);
    return uploadFileResponse;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const saveBookAuthorName = async (authorName, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/author_name`,
      {
        author_name: authorName,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save author name");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save author name:", error);
    throw error;
  }
};

export const saveBookAuthorBio = async (authorBio, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/author_bio`,
      {
        bio: authorBio,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save author bio");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save author bio:", error);
    throw error;
  }
};

export const generateBookAuthorBio = async (bookId) => {
  try {
    const response = await axiosInstance.get(
      `/manuscripts/${bookId}/regenerate/author_bio`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to generate author bio");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to generate author bio:", error);
    throw error;
  }
};

export const saveBookLogline = async (logline, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/logline`,
      {
        logline,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save logline");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save logline:", error);
    throw error;
  }
};

export const generateBookLogline = async (bookId) => {
  try {
    // TODO: this should be a get, not a post
    const response = await axiosInstance.get(
      `/manuscripts/${bookId}/regenerate/logline`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to generate logline");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save logline:", error);
    throw error;
  }
};

export const saveBookKeywords = async (keywords, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/keywords`,
      {
        keywords,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save keywords");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save keywords:", error);
    throw error;
  }
};

export const generateBookKeywords = async (bookId) => {
  try {
    const response = await axiosInstance.get(
      `/manuscripts/${bookId}/regenerate/keywords`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to generate keywords");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to generate keywords:", error);
    throw error;
  }
};

export const saveBookGenres = async (genres, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/genres`,
      {
        genres,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save genres");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save genres:", error);
    throw error;
  }
};

export const generateBookGenres = async (bookId) => {
  try {
    const response = await axiosInstance.get(
      `/manuscripts/${bookId}/regenerate/genres`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to generate genres");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to generate genres:", error);
    throw error;
  }
};

export const saveBookTitle = async (title, bookId) => {
  try {
    const response = await axiosInstance.patch(`/manuscripts/${bookId}/title`, {
      book_title: title,
    });
    if (response.status !== 200) {
      throw new Error("Failed to save title");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save title:", error);
    throw error;
  }
};

export const saveBookCover = async (cover, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/cover`,
      { cover },
      { timeout: 100000 },
    );

    if (response.status !== 200) {
      throw new Error("Failed to save cover");
    }

    console.log("Book cover saved successfully:", { bookId, cover });

    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save cover:", error);
    throw error;
  }
};

export const generateBookCover = async (bookId) => {
  try {
    // TODO: this should be a get, not a post
    const response = await axiosInstance.get(
      `/manuscripts/${bookId}/regenerate/cover`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to generate cover");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save cover:", error);
    throw error;
  }
};

// Add a simple cache to prevent duplicate requests
const requestCache = new Map();

export const getBookTrailer = async (bookId) => {
  const cacheKey = `trailer-${bookId}`;

  // If there's already a pending request for this trailer, return that promise
  if (requestCache.has(cacheKey)) {
    console.log("🎬 API: Returning cached trailer request for bookId:", bookId);
    return requestCache.get(cacheKey);
  }

  const requestPromise = (async () => {
    try {
      console.log("🎬 API: Making new trailer request for bookId:", bookId);
      const response = await axiosInstance.get(
        `/manuscripts/${bookId}/trailer`,
      );
      if (response.status !== 200) {
        throw new Error("Failed to get trailer");
      }
      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to get trailer:", error);
      throw error;
    } finally {
      // Remove from cache when request completes (success or failure)
      requestCache.delete(cacheKey);
    }
  })();

  // Store the request promise in cache
  requestCache.set(cacheKey, requestPromise);

  return requestPromise;
};

export const saveBookTrailer = async (trailerUrl, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/trailer`,
      {
        trailer_url: trailerUrl,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save trailer");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save trailer:", error);
    throw error;
  }
};

export const generateBookTrailer = async (bookId) => {
  const startTime = Date.now();
  console.log("🎬 API: Starting video generation for bookId:", bookId);

  try {
    const response = await axiosInstance.post(
      `/manuscripts/${bookId}/trailer/generate`,
    );

    const duration = Date.now() - startTime;
    console.log(
      "🎬 API: Response received in",
      `${duration}ms`,
      "Status:",
      response.status,
    );
    console.log("🎬 API: Response data:", response.data);

    if (response.status !== 200) {
      throw new Error("Failed to generate trailer");
    }

    // Check if response data indicates an error (backend returning errors with 200 status)
    if (Array.isArray(response.data) && response.data.length >= 2) {
      const [statusCode, errorMessage] = response.data;
      if (statusCode >= 400) {
        console.error("🎬 API: Backend error detected:", {
          statusCode,
          errorMessage,
        });
        let parsedError;
        try {
          parsedError = JSON.parse(errorMessage);
        } catch (e) {
          parsedError = { error_msg: errorMessage };
        }
        throw new Error(
          `Video generation failed: ${parsedError.error_msg || errorMessage}`,
        );
      }
    }

    // Check for error_msg in response data
    if (response.data && response.data.error_msg) {
      console.error("🎬 API: Error in response:", response.data.error_msg);
      throw new Error(`Video generation failed: ${response.data.error_msg}`);
    }

    console.log("🎬 API: Video generation completed successfully");
    return response.data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error("🎬 API: Video generation failed after", `${duration}ms`);
    console.error("🎬 API: Error:", error.message);

    if (error.response) {
      console.error(
        "🎬 API: Error response:",
        error.response.status,
        error.response.data,
      );
    }

    Sentry.captureException(error);
    console.error("Failed to generate trailer:", error);
    throw error;
  }
};

export const saveBookCritique = async (critique, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/critique`,
      {
        critique,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save critique");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save critique:", error);
    throw error;
  }
};

export const getCritique = async (bookId) => {
  const response = await axiosInstance.get(`/manuscripts/${bookId}/critique`);
  return response.data;
};

export const triggerCritiqueGeneration = async (bookId) => {
  const response = await axiosInstance.post(
    `/manuscripts/${bookId}/generate_critique`,
  );
  return response.data;
};

export const getCritiqueStatus = async (bookId) => {
  const response = await axiosInstance.get(
    `/manuscripts/${bookId}/critique_status`,
  );
  return response.data;
};

export const saveBookSynopsis = async (synopsis, bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/synopsis`,
      {
        synopsis: synopsis,
        synopsis_markdown: synopsis,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save synopsis");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save synopsis:", error);
    throw error;
  }
};

export const generateBookSynopsis = async (bookId) => {
  try {
    const response = await axiosInstance.get(
      `/manuscripts/${bookId}/regenerate/synopsis`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to generate synopsis");
    }
    return response.data.synopsis_markdown;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to generate synopsis:", error);
    throw error;
  }
};

export const saveSimilarBooks = async (bookId, isbns) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/similar_books`,
      {
        isbns,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to save similar books");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to save similar books:", error);
    throw error;
  }
};

export const deleteSimilarBook = async (bookId, isbn) => {
  try {
    const response = await axiosInstance.delete(
      `/manuscripts/${bookId}/similar_books/${isbn}`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to delete similar book");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to delete similar book:", error);
    throw error;
  }
};

export const updateDiscovery = async (bookId, discovery) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/discovery`,
      {
        discovery,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to update discovery status");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to update discovery status:", error);
    throw error;
  }
};

export const removeManuscript = async (bookId) => {
  try {
    const response = await axiosInstance.delete(`/manuscripts/${bookId}`);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to remove manuscript:", error);
    throw error;
  }
};

export const updateAdvanceRequest = async (bookId) => {
  try {
    const response = await axiosInstance.patch(
      `/manuscripts/${bookId}/advance_request`,
      {
        advance_requested: true,
      },
    );
    if (response.status !== 200) {
      throw new Error("Failed to update advance request");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to update advance request:", error);
    throw error;
  }
};

export const updateUserInfo = async (fullName, email) => {
  try {
    const updateData = {
      full_name: fullName,
      email: email,
    };
    const response = await axiosInstance.patch("/users/me", updateData);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    if (error.response) {
      switch (error.response.status) {
        case 404:
          throw new Error("User not found");
        case 400:
          throw new Error("No fields provided for update");
        case 409:
          throw new Error("Email address is already in use");
        default:
          throw new Error("Failed to update user info");
      }
    }
    throw new Error("Network error occurred");
  }
};

export const createSampleBook = async () => {
  try {
    const response = await axiosInstance.post(
      "/manuscripts/create_sample_book",
    );
    if (response.status !== 201) {
      throw new Error("Failed to create sample book");
    }
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to create sample book:", error);
    throw error;
  }
};

export const trackReferral = async (
  utmSource,
  utmMedium,
  utmCampaign,
  referrer,
) => {
  try {
    const response = await axiosInstance.post("/users/referral", {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      referrer: referrer,
    });
    const referralId = response.data.referral_id;
    console.log("Referral ID:", referralId);
    return referralId;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to record referral", error);
  }
};

export const approvePublisherAccess = async (bookId, companyId) => {
  try {
    const response = await axiosInstance.post(
      `/swn/${bookId}/${companyId}/approve_access`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to approve publisher access");
    }
    console.log("Publisher access approved:", response.data);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to approve publisher access:", error);
    throw error;
  }
};

export const rejectPublisherAccess = async (bookId, companyId) => {
  try {
    const response = await axiosInstance.post(
      `/swn/${bookId}/${companyId}/reject_access`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to reject publisher access");
    }
    console.log("Publisher access rejected:", response.data);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to reject publisher access:", error);
    throw error;
  }
};

export const uploadBookCover = async (file, bookId) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post(
    `/manuscripts/${bookId}/upload_image/cover`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 100000,
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to upload cover image");
  }

  console.log("Book cover uploaded successfully:", response.data.url);

  return response.data.url; // S3 URL from backend
};

/**
 * Get a presigned S3 URL for a book cover image.
 * @param {string} fileName - The S3 file name (e.g. "covers_dir/{book_id}_gptimage1.webp").
 * @returns {Promise<string>} - The presigned URL.
 */
export const downloadBookCoverImagePreSignedURL = async (fileName) => {
  try {
    // Do NOT encode fileName, just pass as is
    const response = await axiosInstance.get(
      `/manuscripts/book-cover-presigned-url/${fileName}`,
    );
    if (response.status !== 200) {
      throw new Error("Failed to get presigned book cover URL");
    }

    console.log(
      "Presigned book cover URL retrieved successfully:",
      response.data.presigned_url,
    );

    return response.data.presigned_url;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};
